// kernels.js
import * as d3 from "d3";

// ---- Kernel functions K(u): integrate to 1 over R (or support) ----
export const kernels = {
  gaussian: (u) => (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * u * u),
  epanechnikov: (u) => (Math.abs(u) <= 1 ? 0.75 * (1 - u * u) : 0),
  uniform: (u) => (Math.abs(u) <= 1 ? 0.5 : 0),
  triangular: (u) => (Math.abs(u) <= 1 ? 1 - Math.abs(u) : 0),
  quartic: (u) => (Math.abs(u) <= 1 ? (15 / 16) * (1 - u * u) ** 2 : 0), // biweight
  triweight: (u) => (Math.abs(u) <= 1 ? (35 / 32) * (1 - u * u) ** 3 : 0),
  cosine: (u) => (Math.abs(u) <= 1 ? (Math.PI / 4) * Math.cos((Math.PI / 2) * u) : 0),
  silverman: (u) => 0.5 * Math.exp(-Math.abs(u) / Math.SQRT2) * Math.sin(Math.abs(u) / Math.SQRT2 + Math.PI / 4), // optional
};

// ---- Silverman's rule-of-thumb bandwidth (univariate) ----
// h = 0.9 * min(sd, IQR/1.34) * n^(-1/5)
export function silvermanBandwidth(values) {
  const n = values.length;
  if (n < 2) return 1; // fallback
  const sd = d3.deviation(values);
  const q25 = d3.quantileSorted([...values].sort(d3.ascending), 0.25);
  const q75 = d3.quantileSorted([...values].sort(d3.ascending), 0.75);
  const iqr = q75 - q25;
  const scale = Math.min(sd ?? 0, iqr / 1.34 || 0) || (sd || 1);
  return 0.9 * scale * Math.pow(n, -1 / 5);
}

// ---- Generic KDE estimator ----
// V: data array, X: evaluation points, K: kernel(u), h: bandwidth
export function kdeEstimator(K, h, X, V) {
  const n = V.length;
  const invNh = 1 / (n * h);
  return X.map((x) => {
    const y = d3.mean(V, (v) => K((x - v) / h)) * invNh;
    return [x, y];
  });
}

// ---- Convenience: build X over data range ----
export function makeGrid(values, n = 200, pad = 0.05) {
  const min = d3.min(values);
  const max = d3.max(values);
  if (min === undefined || max === undefined) return [];
  const span = max - min || 1;
  const a = min - pad * span;
  const b = max + pad * span;
  return d3.range(0, n).map((i) => a + (i * (b - a)) / (n - 1));
}
